import { Link } from 'react-router-dom';
import { ChevronLeft, ClipboardCheck, FileText, Image, ListTodo } from 'lucide-react';
import { StatusBadge } from '@/design/components';
import { ModuleGate } from '@/access/Gate';
import { useModules } from '@/access/useModule';
import { useMe } from '@/auth/useMe';
import { usePendingSignatures } from '@/features/documents/api';
import { isOverdue, useTasks } from './api';

/**
 * מה צריך לעשות היום (PRD 5.9).
 *
 * קיצורים למה שעובד פותח בתחילת משמרת, כי הסרגל התחתון מלא.
 * המספר שליד "משימות" הוא מה שפתוח עליו עכשיו, ולא סך הכל.
 *
 * לכל קיצור המפתח שלו. קודם ישב כאן שער אחד של "ops" על כולם,
 * ומי שכיבה צ׳קליסטים לבדם ראה אותם ממשיכים להופיע לעובדים —
 * הכפתור נשאר, והלחיצה עליו החזירה הביתה בלי הסבר.
 */
export function OpsCard() {
  return (
    <ModuleGate module="ops" fallback={null}>
      <OpsCardInner />
    </ModuleGate>
  );
}

function OpsCardInner() {
  const { me } = useMe();
  const modules = useModules();
  const tasks = useTasks();
  const pending = usePendingSignatures();

  const mine = (tasks.data ?? []).filter(
    (task) => task.assignee_id === me?.employeeId && !task.completed_at,
  );
  const late = mine.filter(isOverdue).length;

  // כבו את כולם — גם הכותרת הולכת. כרטיס ריק עם כותרת נראה
  // כמו תקלה.
  if (
    !modules.ops_checklists &&
    !modules.ops_display &&
    !modules.ops_documents &&
    !modules.ops_tasks
  ) {
    return null;
  }

  return (
    <section className="grid gap-2 rounded-lg bg-raised p-4 shadow-card">
      <h2 className="m-0 font-display text-display-m font-semibold">המשמרת שלי</h2>

      {modules.ops_checklists && (
        <Link
          to="/checklists"
          className="flex items-center gap-2 rounded-md border border-line p-3 text-body font-medium text-ink no-underline"
        >
          <ClipboardCheck size={20} strokeWidth={1.75} aria-hidden="true" />
          <span className="grow">צ׳קליסט המשמרת</span>
          <ChevronLeft size={20} strokeWidth={1.75} aria-hidden="true" className="text-muted" />
        </Link>
      )}

      {modules.ops_display && (
        <Link
          to="/display"
          className="flex items-center gap-2 rounded-md border border-line p-3 text-body font-medium text-ink no-underline"
        >
          <Image size={20} strokeWidth={1.75} aria-hidden="true" />
          <span className="grow">ויטרינה</span>
          <ChevronLeft size={20} strokeWidth={1.75} aria-hidden="true" className="text-muted" />
        </Link>
      )}

      {modules.ops_documents && (
        <Link
          to="/documents"
          className="flex items-center gap-2 rounded-md border border-line p-3 text-body font-medium text-ink no-underline"
        >
          <FileText size={20} strokeWidth={1.75} aria-hidden="true" />
          <span className="grow">נהלים</span>
          {(pending.data?.length ?? 0) > 0 && (
            <StatusBadge tone="warn">{pending.data?.length} לחתימה</StatusBadge>
          )}
          <ChevronLeft size={20} strokeWidth={1.75} aria-hidden="true" className="text-muted" />
        </Link>
      )}

      {modules.ops_tasks && (
        <Link
          to="/tasks"
          className="flex items-center gap-2 rounded-md border border-line p-3 text-body font-medium text-ink no-underline"
        >
          <ListTodo size={20} strokeWidth={1.75} aria-hidden="true" />
          <span className="grow">
            המשימות שלי
            {mine.length > 0 && (
              <span className="ms-1 text-body-sm font-normal text-muted">({mine.length})</span>
            )}
          </span>
          {late > 0 && <StatusBadge tone="bad">{late} באיחור</StatusBadge>}
          <ChevronLeft size={20} strokeWidth={1.75} aria-hidden="true" className="text-muted" />
        </Link>
      )}
    </section>
  );
}
